import React, { useState, useEffect } from 'react';
import styles from './UsageAnalytics.module.css';
import { backgroundProcessService } from '../../../services/api';

const UsageAnalytics = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [usageTab, setUsageTab] = useState('breakdown');

  useEffect(() => {
    fetchUsageAnalytics();
  }, []);

  const fetchUsageAnalytics = async () => {
    try {
      setLoading(true);
      const response = await backgroundProcessService.getUsageAnalytics();
      
      if (response.data.status === 'success') {
        setAnalyticsData(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch usage analytics');
      }
    } catch (err) {
      console.error('Error fetching usage analytics:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch usage analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateDuration = (startedAt, completedAt) => {
    if (!startedAt || !completedAt) return null;
    
    const start = new Date(startedAt);
    const end = new Date(completedAt);
    const durationMs = end - start;
    
    if (durationMs <= 0) return null;
    
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };





  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}>
          <i className="fas fa-spinner fa-spin"></i>
        </div>
        <p>Loading your usage analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorIcon}>
          <i className="fas fa-exclamation-triangle"></i>
        </div>
        <h3>Error Loading Analytics</h3>
        <p>{error}</p>
        <button 
          className={styles.retryButton}
          onClick={fetchUsageAnalytics}
        >
          <i className="fas fa-redo"></i>
          Try Again
        </button>
      </div>
    );
  }

  if (!analyticsData || analyticsData.comparisons.length === 0) {
    return (
      <div className={styles.noDataContainer}>
        <div className={styles.noDataIcon}>
          <i className="fas fa-chart-bar"></i>
        </div>
        <h3>No Usage Data Available</h3>
        <p>Start creating comparisons to see your usage analytics here.</p>
        <div className={styles.noDataActions}>
          <button 
            className={styles.createComparisonButton}
            onClick={() => window.location.href = '/homeowner/create-comparison'}
          >
            <i className="fas fa-plus"></i>
            Create Your First Comparison
          </button>
        </div>
      </div>
    );
  }

  const { comparisons, usageStats, monthlyUsage } = analyticsData;

  return (
    <div className={styles.usageAnalytics}>
      <div className={styles.header}>
        <h2>Usage Analytics</h2>
        <p>Track your Trakvu usage and comparison history</p>
      </div>

      <div className={styles.tabNavigation}>
        <button 
          className={`${styles.tabButton} ${activeTab === 'overview' ? styles.active : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <i className="fas fa-chart-pie"></i>
          Overview
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'usage' ? styles.active : ''}`}
          onClick={() => setActiveTab('usage')}
        >
          <i className="fas fa-list-ul"></i>
          Usage Breakdown
        </button>

        <button 
          className={`${styles.tabButton} ${activeTab === 'trends' ? styles.active : ''}`}
          onClick={() => setActiveTab('trends')}
        >
          <i className="fas fa-chart-line"></i>
          Trends
        </button>
      </div>

      <div className={styles.content}>
        {activeTab === 'overview' && (
          <div className={styles.overviewTab}>
            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <div className={styles.statIcon}>
                  <i className="fas fa-file-contract"></i>
                </div>
                <div className={styles.statContent}>
                  <h3>{usageStats.total_comparisons}</h3>
                  <p>Total Comparisons</p>
                </div>
              </div>

              <div className={styles.statCard}>
                <div className={styles.statIcon}>
                  <i className="fas fa-crown"></i>
                </div>
                <div className={styles.statContent}>
                  <h3>{usageStats.premium_analyses}</h3>
                  <p>Premium Analyses</p>
                </div>
              </div>

              <div className={styles.statCard}>
                <div className={styles.statIcon}>
                  <i className="fas fa-calendar-week"></i>
                </div>
                <div className={styles.statContent}>
                  <h3>{usageStats.comparisons_last_7_days}</h3>
                  <p>Last 7 Days</p>
                </div>
              </div>

              <div className={styles.statCard}>
                <div className={styles.statIcon}>
                  <i className="fas fa-calendar-alt"></i>
                </div>
                <div className={styles.statContent}>
                  <h3>{usageStats.comparisons_last_30_days}</h3>
                  <p>Last 30 Days</p>
                </div>
              </div>
            </div>



            <div className={styles.recentActivity}>
              <h3>Recent Activity</h3>
              <div className={styles.activityList}>
                {analyticsData.usageBreakdown && analyticsData.usageBreakdown.length > 0 ? (
                  analyticsData.usageBreakdown.slice(0, 5).map((item, index) => (
                    <div key={index} className={styles.activityItem}>
                      <div className={styles.activityIcon}>
                        <i className="fas fa-file-contract"></i>
                      </div>
                      <div className={styles.activityContent}>
                        <h4>{item.projectName}</h4>
                        <p>{item.usageCategory} - {item.usageDescription}</p>
                        <span className={styles.activityDate}>
                          {item.metadata && item.metadata.createdAt ? 
                            formatDate(item.metadata.createdAt) : 
                            'Recent activity'
                          }
                        </span>
                      </div>
                      <div className={styles.premiumBadge}>
                        <i className="fas fa-crown"></i>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={styles.noActivityData}>
                    <p>No recent usage activity. Start using premium features to see your activity here.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'usage' && (
          <div className={styles.usageBreakdownTab}>
            <div className={styles.usageBreakdownHeader}>
              <h3>Detailed Usage Breakdown</h3>
              <p>See exactly what you used your comparisons for</p>
            </div>

            {analyticsData.usageBreakdown && analyticsData.usageBreakdown.length > 0 ? (
              <div className={styles.usageBreakdownList}>
                                 {analyticsData.usageBreakdown.map((item, index) => (
                                        <div key={index} className={styles.usageItem}>
                       <div className={styles.usageHeader}>
                         <div className={styles.usageInfo}>
                           <h4 className={styles.projectName}>{item.projectName}</h4>
                           <p className={styles.usageCategory}>{item.usageCategory}</p>
                           <p className={styles.usageDescription}>{item.usageDescription}</p>
                           <div className={styles.usageTimestamps}>
                             <span className={styles.timestamp}>
                               <i className="fas fa-play"></i>
                               Started: {item.started_at ? formatDateTime(item.started_at) : 'N/A'}
                             </span>
                             {item.completed_at && (
                               <span className={styles.timestamp}>
                                 <i className="fas fa-check"></i>
                                 Completed: {formatDateTime(item.completed_at)}
                               </span>
                             )}
                             {item.started_at && item.completed_at && (
                               <span className={styles.timestamp}>
                                 <i className="fas fa-clock"></i>
                                 Duration: {calculateDuration(item.started_at, item.completed_at)}
                               </span>
                             )}
                           </div>
                         </div>
                         <div className={styles.usageStats}>
                           <div className={styles.usageStatus}>
                             <span className={`${styles.statusBadge} ${styles.completed}`}>
                               {item.completed_count} completed
                             </span>
                             {item.failed_count > 0 && (
                               <span className={`${styles.statusBadge} ${styles.failed}`}>
                                 {item.failed_count} failed
                               </span>
                             )}
                           </div>
                         </div>
                       </div>
                    
                    {item.metadata && Object.keys(item.metadata).length > 0 && (
                      <div className={styles.usageMetadata}>
                        <details>
                          <summary>View Details</summary>
                          <div className={styles.metadataContent}>
                            {Object.entries(item.metadata).map(([key, value]) => (
                              <div key={key} className={styles.metadataItem}>
                                <strong>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</strong>
                                <span>{typeof value === 'string' ? value : JSON.stringify(value)}</span>
                              </div>
                            ))}
                          </div>
                        </details>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.noUsageData}>
                <div className={styles.noDataIcon}>
                  <i className="fas fa-info-circle"></i>
                </div>
                <h4>No Detailed Usage Data</h4>
                <p>Usage breakdown will appear here as you use more features.</p>
              </div>
            )}
          </div>
        )}





        {activeTab === 'trends' && (
          <div className={styles.trendsTab}>
            <div className={styles.trendsHeader}>
              <h3>Advanced Usage Analytics</h3>
              <p>Real-time insights into your usage patterns and behavior</p>
            </div>

            {/* Process Type Analysis */}
            {analyticsData.trends?.processTypeTrends?.length > 0 && (
              <div className={styles.analyticsSection}>
                <h4>Process Type Usage</h4>
                <div className={styles.processTypeGrid}>
                  {analyticsData.trends.processTypeTrends.map((item, index) => (
                    <div key={index} className={styles.processTypeCard}>
                      <div className={styles.processTypeHeader}>
                        <h5>{item.process_type.replace(/_/g, ' ').toUpperCase()}</h5>
                        <span className={styles.processCount}>{item.usage_count}</span>
                      </div>
                      <div className={styles.processTypeStats}>
                        <div className={styles.statItem}>
                          <span className={styles.statLabel}>Success Rate</span>
                          <span className={styles.statValue}>{item.successRate}%</span>
                        </div>
                        <div className={styles.statItem}>
                          <span className={styles.statLabel}>Completed</span>
                          <span className={styles.statValue}>{item.completed_count}</span>
                        </div>
                        {item.failed_count > 0 && (
                          <div className={styles.statItem}>
                            <span className={styles.statLabel}>Failed</span>
                            <span className={styles.statValue.error}>{item.failed_count}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Change Type Analysis */}
            {analyticsData.trends?.changeTypeAnalysis?.length > 0 && (
              <div className={styles.analyticsSection}>
                <h4>Analysis Type Breakdown</h4>
                <div className={styles.changeTypeChart}>
                  {analyticsData.trends.changeTypeAnalysis.map((item, index) => (
                    <div key={index} className={styles.changeTypeBar}>
                      <div className={styles.changeTypeLabel}>
                        <span>{item.changeTypeLabel}</span>
                        <span className={styles.changeTypeCount}>{item.count}</span>
                      </div>
                      <div className={styles.changeTypeProgress}>
                        <div 
                          className={styles.changeTypeFill}
                          style={{ 
                            width: `${(item.count / Math.max(...analyticsData.trends.changeTypeAnalysis.map(c => c.count))) * 100}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Weekly Patterns */}
            {analyticsData.trends?.weeklyPatterns?.length > 0 && (
              <div className={styles.analyticsSection}>
                <h4>Weekly Usage Patterns</h4>
                <div className={styles.weeklyChart}>
                  {analyticsData.trends.weeklyPatterns.map((item, index) => (
                    <div key={index} className={styles.weeklyBar}>
                      <div className={styles.weeklyBarContainer}>
                        <div 
                          className={styles.weeklyBarFill}
                          style={{ 
                            height: `${(item.usage_count / Math.max(...analyticsData.trends.weeklyPatterns.map(w => w.usage_count))) * 100}%` 
                          }}
                        ></div>
                      </div>
                      <div className={styles.weeklyLabel}>
                        <span className={styles.weeklyDay}>{item.dayName.slice(0, 3)}</span>
                        <span className={styles.weeklyCount}>{item.usage_count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Hourly Patterns */}
            {analyticsData.trends?.hourlyPatterns?.length > 0 && (
              <div className={styles.analyticsSection}>
                <h4>Hourly Usage Patterns</h4>
                <div className={styles.hourlyChart}>
                  {analyticsData.trends.hourlyPatterns.map((item, index) => (
                    <div key={index} className={styles.hourlyBar}>
                      <div className={styles.hourlyBarContainer}>
                        <div 
                          className={styles.hourlyBarFill}
                          style={{ 
                            height: `${(item.usage_count / Math.max(...analyticsData.trends.hourlyPatterns.map(h => h.usage_count))) * 100}%` 
                          }}
                        ></div>
                      </div>
                      <div className={styles.hourlyLabel}>
                        <span className={styles.hourlyTime}>{item.hour_of_day}:00</span>
                        <span className={styles.hourlyCount}>{item.usage_count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Activity Timeline */}
            {analyticsData.trends?.recentActivity?.length > 0 && (
              <div className={styles.analyticsSection}>
                <h4>Recent Activity Timeline</h4>
                <div className={styles.activityTimeline}>
                  {analyticsData.trends.recentActivity.slice(0, 10).map((item, index) => (
                    <div key={index} className={styles.timelineItem}>
                      <div className={styles.timelineIcon}>
                        <i className={`fas fa-${item.process_type === 'premium_analysis' ? 'crown' : 'cog'}`}></i>
                      </div>
                      <div className={styles.timelineContent}>
                        <div className={styles.timelineHeader}>
                          <h6>{item.project_name || 'Unknown Project'}</h6>
                          <span className={`${styles.timelineStatus} ${styles[item.status]}`}>
                            {item.status}
                          </span>
                        </div>
                        <p className={styles.timelineDetails}>
                          {item.process_type.replace(/_/g, ' ')} 
                          {item.change_type && ` - ${item.change_type.replace(/_/g, ' ')}`}
                          {item.duration && ` (${item.duration} min)`}
                        </p>
                        <span className={styles.timelineTime}>
                          {formatDateTime(item.started_at)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Enhanced Monthly Chart */}
            <div className={styles.analyticsSection}>
              <h4>Monthly Usage Overview</h4>
              <div className={styles.enhancedMonthlyChart}>
                {monthlyUsage.length > 0 ? (
                  <div className={styles.chartContainer}>
                    <div className={styles.chartBars}>
                      {monthlyUsage.map((month) => (
                        <div key={month.month} className={styles.chartBar}>
                          <div className={styles.barContainer}>
                            <div 
                              className={styles.barFill}
                              style={{ 
                                height: `${Math.max((month.comparisons_count / Math.max(...monthlyUsage.map(m => m.comparisons_count))) * 100, 10)}%` 
                              }}
                            ></div>
                          </div>
                          <div className={styles.barLabel}>
                            <span className={styles.monthLabel}>
                              {new Date(month.month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}
                            </span>
                            <span className={styles.countLabel}>{month.comparisons_count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className={styles.noTrendsData}>
                    <i className="fas fa-chart-line"></i>
                    <p>No trend data available yet. Start using premium features to see your patterns.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Key Insights */}
            <div className={styles.insightsGrid}>
              <div className={styles.insightCard}>
                <h4>Most Active Month</h4>
                {monthlyUsage.length > 0 ? (
                  <div className={styles.insightValue}>
                    {(() => {
                      const mostActive = monthlyUsage.reduce((max, current) => 
                        current.comparisons_count > max.comparisons_count ? current : max
                      );
                      return (
                        <>
                          <span className={styles.insightNumber}>{mostActive.comparisons_count}</span>
                          <span className={styles.insightLabel}>
                            {new Date(mostActive.month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <p>No data available</p>
                )}
              </div>

              <div className={styles.insightCard}>
                <h4>Average Monthly Usage</h4>
                {monthlyUsage.length > 0 ? (
                  <div className={styles.insightValue}>
                    <span className={styles.insightNumber}>
                      {Math.round(monthlyUsage.reduce((sum, month) => sum + month.comparisons_count, 0) / monthlyUsage.length)}
                    </span>
                    <span className={styles.insightLabel}>comparisons per month</span>
                  </div>
                ) : (
                  <p>No data available</p>
                )}
              </div>

              <div className={styles.insightCard}>
                <h4>Most Used Feature</h4>
                {analyticsData.trends?.processTypeTrends?.length > 0 ? (
                  <div className={styles.insightValue}>
                    <span className={styles.insightNumber}>
                      {analyticsData.trends.processTypeTrends[0].usage_count}
                    </span>
                    <span className={styles.insightLabel}>
                      {analyticsData.trends.processTypeTrends[0].process_type.replace(/_/g, ' ')}
                    </span>
                  </div>
                ) : (
                  <p>No data available</p>
                )}
              </div>

              <div className={styles.insightCard}>
                <h4>Peak Usage Day</h4>
                {analyticsData.trends?.weeklyPatterns?.length > 0 ? (
                  <div className={styles.insightValue}>
                    {(() => {
                      const peakDay = analyticsData.trends.weeklyPatterns.reduce((max, current) => 
                        current.usage_count > max.usage_count ? current : max
                      );
                      return (
                        <>
                          <span className={styles.insightNumber}>{peakDay.usage_count}</span>
                          <span className={styles.insightLabel}>{peakDay.dayName}</span>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <p>No data available</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsageAnalytics; 